import React, { useEffect, useRef, useState } from "react";
import { AfDataType } from "utils/TableColumnData";
import moment from "moment";
import UseToast from "utils/AfToast";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { ListCommanWay } from "API/commonApi";
import Spinner from "utils/Spinner";

const EmailTrail = ({ emailTrail }) => {
  // State to track which emails' quoted content is expanded
  const [expanded, setExpanded] = useState({});

  // Function to parse and split email body into new and quoted content
  const parseEmailBody = (html) => {
    // Parse HTML string into a DOM object
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || "<div></div>", "text/html");

    // Identify quoted content (common patterns: <blockquote>, <div class="gmail_quote">, or <div> with specific styles)
    const quotedSelectors = [
      "blockquote", // Standard HTML quotes
      'div[class*="quote"]', // Gmail: gmail_quote
      // Outlook: specific border styles
      //'div[style*="padding-left: 4.0pt; border-left: solid"]', // Outlook variations,
    ];
    const quotedElements = doc.querySelectorAll(quotedSelectors.join(","));

    let quotedContent = "";
    let newContent = html;

    if (quotedElements.length > 0) {
      quotedContent = Array.from(quotedElements)
        .map((el) => el.outerHTML)
        .join("");
      quotedElements.forEach((el) => el.remove());
      newContent = doc.body.innerHTML || html;
      return { newContent, quotedContent };
    }

    // Step 2: Separator-based detection (Outlook’s <hr> or border-top)
    const separators = doc.querySelectorAll('div[id*="divRplyFwdMsg"]');
    if (separators.length > 0) {
      const separator = separators[0]; // Use first separator
      const parent = separator.parentNode;
      const nodes = Array.from(parent.childNodes);
      const separatorIndex = nodes.indexOf(separator);

      // Split content before and after separator
      const newNodes = nodes.slice(0, separatorIndex);
      const quotedNodes = nodes.slice(separatorIndex + 1);
      newContent = newNodes.map((node: any) => node.outerHTML || node.textContent).join("") || "<p></p>";
      quotedContent = quotedNodes.map((node: any) => node.outerHTML || node.textContent).join("") || "";
      return { newContent, quotedContent };
    }

    // Step 3: Text-based detection for metadata or quote markers
    const textContent = doc.body.textContent || "";
    const quoteMarkers = [
      /From:.*Sent:.*To:/i, // Outlook metadata
      /\n> /, // Plain text quote marker
      /On .* wrote:/i, // Gmail/others
    ];
    for (const marker of quoteMarkers) {
      const matchIndex = textContent.search(marker);
      if (matchIndex !== -1) {
        // Approximate split in HTML based on text position
        const htmlArray = html.split("\n");
        const textArray = textContent.split("\n");
        let splitLine = 0;
        let textPos = 0;

        for (let i = 0; i < textArray.length; i++) {
          textPos += textArray[i].length + 1;
          if (textPos > matchIndex) {
            splitLine = i;
            break;
          }
        }

        newContent = htmlArray.slice(0, splitLine).join("\n") || "<p></p>";
        quotedContent = htmlArray.slice(splitLine).join("\n") || "";
        return { newContent, quotedContent };
      }
    }
    return { newContent, quotedContent };
  };
  const toggleExpand = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div>
      {emailTrail &&
        emailTrail.map((email, index) => {
          // Parse email body into new and quoted content
          const { newContent, quotedContent } = parseEmailBody(email.body);

          return (
            <div key={index} className="border-b p-2 border-t-2 rounded-lg bg-white mt-4">
              <p className="bg-gray-700 bg-opacity-10 mb-2 px-2 rounded-md text-sm">From: {email.from}</p>
              <p className="bg-gray-700 bg-opacity-10 mb-2 px-2 rounded-md text-sm">CC: {email.ccRecipients?.join(", ")}</p>
              <p className="bg-gray-700 bg-opacity-10 mb-2 px-2 rounded-md text-sm">Reply to: {email.replyTo?.join(", ")}</p>
              <p className="text-sm text-gray-500">Date: {moment(email.receivedDateTime).format("MMMM Do YYYY, h:mm:ss a")}</p>
              <div className="email-body">
                {/* Render new content */}
                <div dangerouslySetInnerHTML={{ __html: newContent }} />
                {/* Render quoted content (collapsed by default) */}
                {quotedContent && (
                  <div>
                    {expanded[index] ? (
                      <>
                        <div dangerouslySetInnerHTML={{ __html: quotedContent }} />
                        <button className="text-blue-500 text-sm mt-2" onClick={() => toggleExpand(index)}>
                          Collapse quoted text
                        </button>
                      </>
                    ) : (
                      <button className="text-blue-500 text-sm mt-2" onClick={() => toggleExpand(index)}>
                        ... Show quoted text
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
};

const ViewPickupMail: React.FC<any> = ({ pickup_code }) => {
  const ADT = AfDataType;

  const [emailTrail, setEmailTrail] = useState<any[]>([]);

  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((pickup_code) => ListCommanWay("/admin/o365/get-pickup-trail", { pickup_code }, {}), {
    onSuccess: (data: any) => {
      console.log(data, data.status == "SUCCESS");
      if (data.status == "SUCCESS") {
        console.log(data.data.data);
        setEmailTrail(data.data.data);
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  useEffect(() => {
    console.log("Pickup Code:", pickup_code);
    if (pickup_code) {
      mutate(pickup_code);
    }
  }, [pickup_code]);

  const tableRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = localStorage.getItem("auth") ? JSON.parse(localStorage.getItem("auth")) : {};
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  return (
    <>
      <div className="font-inter flex flex-col p-2 m-2 rounded-md">
        {isloading && emailTrail.length ? (
          <Spinner />
        ) : (
          <>
            <div className="bg-white p-2 rounded-md text-lg font-semibold mt-6">
                Subject: {emailTrail[0]?.subject}
            </div>
            <EmailTrail emailTrail={emailTrail} />
          </>
        )}
      </div>
    </>
  );
};

export default ViewPickupMail;
